/**
 * Puts the launcher in the header without the header importing anything.
 *
 * The module renders its own children into a slot element it finds in the DOM.
 * That is what reduces the app's integration surface to a single attribute:
 * `data-tour-slot="header-actions"`.
 *
 * 🔴 IT MUST NEVER RENDER NOTHING.
 * If the slot is absent — removed, renamed, or the header simply is not mounted
 * on this screen — the children fall back to a fixed launcher rather than
 * disappearing. A tour nobody can start is indistinguishable from a broken
 * feature, whereas a relocated button is merely not ideal.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { HOST } from '../engine/anchors';

export function HeaderSlot({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    const find = () => {
      const el = document.querySelector(HOST.headerSlot) as HTMLElement | null;
      // 🔴 A found element is not necessarily a USABLE one. React keeps
      // portaling into a node that has since been detached from the document,
      // which renders the launcher into nothing — visually identical to the
      // feature being missing. `document.contains` is what turns that into the
      // fallback instead.
      const usable = el && document.contains(el) ? el : null;
      if (!cancelled) setSlot((prev) => (prev === usable ? prev : usable));
    };

    find();

    // 🔴 OBSERVE UNCONDITIONALLY, even when the slot was found on the first
    // try. The header mounts after this component on a cold load AND unmounts
    // across auth/route changes, so an observer created only on the miss path
    // would never notice the slot disappearing — the launcher would stay
    // portaled into a detached node and silently vanish.
    const observer = new MutationObserver(find);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // Attributes too: the slot can be removed or renamed without any node
      // being added or removed, which a childList-only observer cannot see.
      attributes: true,
      attributeFilter: ['data-tour-slot'],
    });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  if (slot) return createPortal(children, slot);

  // Fallback: a fixed launcher.
  //
  // 🔴 THE OFFSETS ARE MEASURED, NOT GUESSED. Bottom-right holds the app's own
  // floating launcher, and the bottom-left corner is covered by the sandbox
  // badge (measured at 452×35 px, 12 px from the bottom-left) — a fallback at
  // `bottom: 20` sat underneath it and was completely unclickable, which is
  // worse than being relocated. `bottom: 60` clears the badge; on a build with
  // no badge the corner is empty anyway, so the offset is harmless there.
  return createPortal(
    <div
      data-tour-slot-fallback=""
      // `flex` because the slot holds a group of buttons; and `wrap` with a bounded
      // width because this one is NOT in the header's layout — nothing else would
      // stop it running off the side of a narrow screen.
      style={{
        position: 'fixed',
        bottom: 60,
        left: 20,
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        maxWidth: 'calc(100vw - 40px)',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
